import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute} from '@angular/router';
import {Observable, ReplaySubject} from 'rxjs';
import {first, takeUntil} from 'rxjs/operators';
import {AuthService} from '../../../../shared/services/auth.service';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {ChronieMaintenanceWindow, MaintenanceWindowListItem} from '../../../../shared/interface/chronie-maintenance-window';
import {ChronieMaintenanceWindowService} from '../../../../shared/services/chronie-maintenance-window.service';

@Component({
  selector: 'app-chronie-maintenance-window-view',
  templateUrl: './chronie-maintenance-window-view.component.html',
  styleUrls: ['./chronie-maintenance-window-view.component.scss']
})
export class ChronieMaintenanceWindowViewComponent implements OnInit, OnDestroy {
  @Input() maintenance: Observable<MaintenanceWindowListItem>;
  @Output() modifyClicked = new EventEmitter<any>();

  maintenanceWindowId: string;
  details: ChronieMaintenanceWindow | null = null;
  loading = false;
  processingStatus = false;
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$ = new ReplaySubject<boolean>(1);

  constructor(
    private auth: AuthService,
    private route: ActivatedRoute,
    private chronieMaintenanceWindowService: ChronieMaintenanceWindowService,
    private snackBar: MatSnackBar,
  ) {
    this.userPermissions = auth.getUserPermissions();
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroyed$))
      .subscribe(params => {
        this.maintenanceWindowId = params.id;
        this.loadDetails();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  toggleStatus(): void {
    if (!this.details || this.processingStatus) {
      return;
    }

    const enabled = !this.details.enabled;
    this.processingStatus = true;

    this.chronieMaintenanceWindowService.setStatus(this.details.uuid, enabled)
      .pipe(first())
      .subscribe({
        next: () => {
          this.processingStatus = false;
          this.snackBar.open(
            enabled ? 'Pomyślnie aktywowano konfigurację' : 'Pomyślnie dezaktywowano konfigurację',
            '',
            {duration: 2500}
          );
          this.loadDetails();
        },
        error: (error: HttpErrorResponse) => {
          this.processingStatus = false;
          this.snackBar.open(
            error?.error?.error?.text || 'Nie udało się zmienić statusu',
            error.status.toString(),
            {duration: 5000}
          );
        }
      });
  }

  get statusLabel(): string {
    if (!this.details) {
      return '';
    }

    if (!this.details.enabled) {
      return 'Wyłączona';
    }

    const now = new Date();
    const displayFrom = new Date(this.details.displayFrom);
    const displayTo = this.details.displayTo ? new Date(this.details.displayTo) : null;
    const startsAt = new Date(this.details.startsAt);
    const endsAt = new Date(this.details.endsAt);

    const noticeActive = this.details.noticeEnabled
      && displayFrom <= now
      && (!displayTo || displayTo > now);

    const purchaseBlockActive = this.details.blockPurchases
      && startsAt <= now
      && endsAt > now;

    if (noticeActive || purchaseBlockActive) {
      return 'Aktywna teraz';
    }

    const hasFutureNotice = this.details.noticeEnabled && displayFrom > now;
    const hasFutureBlock = this.details.blockPurchases && startsAt > now;

    if (hasFutureNotice || hasFutureBlock) {
      return 'Zaplanowana';
    }

    return 'Zakończona';
  }

  get blockScope(): string {
    if (!this.details?.blockPurchases) {
      return 'Brak blokady';
    }

    return this.details.insuranceCompany?.name || 'Wszystkie towarzystwa';
  }

  get companyName(): string {
    return this.details?.insuranceCompany?.name || 'Globalny';
  }

  private loadDetails(): void {
    if (!this.maintenanceWindowId) {
      return;
    }

    this.loading = true;
    this.chronieMaintenanceWindowService.get(this.maintenanceWindowId)
      .pipe(first())
      .subscribe({
        next: item => {
          this.details = item;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.snackBar.open('Nie udało się pobrać szczegółów konfiguracji', '', {duration: 4000});
        }
      });
  }


  modify(): void {
    this.modifyClicked.emit();
  }
}
